import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Eye,
  TrendingUp,
  Users,
  Star,
  Crown,
  Globe2,
  MapPin,
  Building2,
  Smartphone,
  Monitor,
  Tablet,
  Bot,
  HelpCircle,
  Compass,
  Link2,
  Calendar,
  X,
  Database,
} from "lucide-react";
import { toast } from "sonner";

const RANGE_OPTIONS = [
  { label: "7 días", value: 7 },
  { label: "30 días", value: 30 },
  { label: "90 días", value: 90 },
  { label: "1 año", value: 365 },
];

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const code = cc.toUpperCase();
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
};

const deviceIcon = (type) => {
  const cls = "w-4 h-4";
  switch ((type || "").toLowerCase()) {
    case "mobile":
      return <Smartphone className={cls} />;
    case "tablet":
      return <Tablet className={cls} />;
    case "desktop":
      return <Monitor className={cls} />;
    case "bot":
      return <Bot className={cls} />;
    default:
      return <HelpCircle className={cls} />;
  }
};

const StatCard = ({ icon: Icon, label, value, accent = "blue", testId }) => {
  const map = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    yellow: "bg-yellow-500/20 text-yellow-400",
    pink: "bg-pink-500/20 text-pink-400",
    orange: "bg-orange-500/20 text-orange-400",
  };
  return (
    <div
      className="bg-white/5 rounded-xl border border-white/10 p-4"
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${map[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl md:text-3xl font-bold text-white">{value ?? 0}</p>
      <p className="text-white/50 text-sm">{label}</p>
    </div>
  );
};

const TopList = ({ title, icon: Icon, items, renderLeft, emptyText, testId }) => {
  const max = items?.[0]?.count || 1;
  return (
    <div
      className="bg-white/5 rounded-xl border border-white/10 p-4 md:p-6"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-white font-semibold">{title}</h3>
      </div>
      {items && items.length > 0 ? (
        <ul className="space-y-3">
          {items.map((it, idx) => {
            const pct = Math.max(4, Math.round((it.count / max) * 100));
            return (
              <li key={`${it.value}-${idx}`} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0 text-white/90">
                    {renderLeft ? renderLeft(it) : <span className="truncate">{it.value || "—"}</span>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-white/70">
                    <span className="tabular-nums text-white">{it.count}</span>
                    <span className="text-xs text-white/40 tabular-nums">
                      {it.unique ?? 0} únicas
                    </span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-500 to-orange-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-white/40 text-sm py-6 text-center">{emptyText || "Sin datos"}</p>
      )}
    </div>
  );
};

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [creatorFilter, setCreatorFilter] = useState("");
  const [creatorList, setCreatorList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [s, c] = await Promise.all([
          fetch("/api/admin/stats", { headers: { ...getAuthHeaders() } }).then((r) => (r.ok ? r.json() : null)),
          fetch("/api/admin/creators", { headers: { ...getAuthHeaders() } }).then((r) => (r.ok ? r.json() : [])),
        ]);
        if (s) setStats(s);
        const list = Array.isArray(c) ? c : c.creators || [];
        setCreatorList(list.filter((x) => x.active !== false));
      } catch (e) {
        console.error("stats error", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [getAuthHeaders]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoadingAnalytics(true);
      try {
        const params = new URLSearchParams({ days: String(days), limit: "10" });
        if (creatorFilter) params.set("username", creatorFilter);
        const response = await fetch(`/api/admin/analytics?${params.toString()}`, {
          headers: { ...getAuthHeaders() },
        });
        if (response.ok) setAnalytics(await response.json());
      } catch (e) {
        console.error("analytics error", e);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [days, creatorFilter, getAuthHeaders]);

  const runBackfill = async () => {
    if (
      !window.confirm(
        "Importará todas las vistas históricas (creator_views) hacia la analítica detallada. Es seguro y se puede repetir. ¿Continuar?"
      )
    )
      return;
    setBackfilling(true);
    try {
      const r = await fetch(`/api/admin/analytics/backfill`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success(
        `📊 ${d.inserted} vistas históricas importadas (de ${d.legacyRows} días legacy)`,
        { description: `Reemplazó ${d.wipedPreviousBackfill} de la importación anterior`, duration: 6000 }
      );
      // re-fetch
      const params = new URLSearchParams({ days: String(days), limit: "10" });
      if (creatorFilter) params.set("username", creatorFilter);
      const aResp = await fetch(`/api/admin/analytics?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      if (aResp.ok) setAnalytics(await aResp.json());
    } catch {
      toast.error("Error de red");
    } finally {
      setBackfilling(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const totals = analytics?.totals || { views: 0, unique: 0 };
  const maxDaily = Math.max(1, ...((analytics?.daily || []).map((d) => d.count)));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Estadísticas</h1>
            <p className="text-white/60">Visualizaciones y rendimiento de tus modelos</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={creatorFilter}
              onChange={(e) => setCreatorFilter(e.target.value)}
              data-testid="analytics-creator-filter"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
            >
              <option value="">Todas las modelos</option>
              {creatorList.map((c) => (
                <option key={c.username} value={c.username}>
                  @{c.username} {c.name ? `· ${c.name}` : ""}
                </option>
              ))}
            </select>
            <div
              className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-1"
              data-testid="analytics-range-selector"
            >
              <Calendar className="w-4 h-4 text-white/50 ml-2" />
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  data-testid={`range-${opt.value}-btn`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    days === opt.value
                      ? "bg-white text-black"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="analytics-backfill-btn"
              className="inline-flex items-center gap-2 px-3 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 rounded-lg text-xs font-medium disabled:opacity-50"
              title="Importar vistas históricas a la analítica detallada"
            >
              <Database className="w-3.5 h-3.5" />
              {backfilling ? "Importando…" : "Importar histórico"}
            </button>
          </div>
        </div>

        {/* Filter banner */}
        {creatorFilter && (
          <div
            className="flex items-center justify-between bg-pink-500/10 border border-pink-500/30 rounded-lg px-4 py-2.5"
            data-testid="creator-filter-banner"
          >
            <p className="text-pink-200 text-sm">
              Mostrando solo: <strong>@{creatorFilter}</strong> · últimos {days} días
              {analytics?.totals && (
                <>
                  {" "}
                  · <span className="text-white">{analytics.totals.views}</span> vistas
                  {" "}
                  · <span className="text-white">{analytics.totals.unique}</span> únicos
                </>
              )}
            </p>
            <button
              onClick={() => setCreatorFilter("")}
              className="inline-flex items-center gap-1 text-pink-200 hover:text-white text-xs"
              data-testid="clear-creator-filter"
            >
              <X className="w-3.5 h-3.5" />
              Quitar filtro
            </button>
          </div>
        )}

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Eye}
            label="Vistas totales"
            value={stats?.totalViews || 0}
            accent="blue"
            testId="stat-total-views"
          />
          <StatCard
            icon={Users}
            label="Total modelos"
            value={stats?.totalCreators || 0}
            accent="green"
            testId="stat-total-creators"
          />
          <StatCard
            icon={TrendingUp}
            label="Activas"
            value={stats?.activeCreators || 0}
            accent="purple"
            testId="stat-active-creators"
          />
          <StatCard
            icon={Star}
            label="Destacadas"
            value={stats?.featuredCreators || 0}
            accent="yellow"
            testId="stat-featured-creators"
          />
        </div>

        {/* Analytics overview cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Eye}
            label={`Vistas (${days}d)`}
            value={totals.views}
            accent="pink"
            testId="stat-period-views"
          />
          <StatCard
            icon={Users}
            label={`Visitantes únicos (${days}d)`}
            value={totals.unique}
            accent="orange"
            testId="stat-period-unique"
          />
          <StatCard
            icon={Globe2}
            label="Países alcanzados"
            value={analytics?.countries?.length || 0}
            accent="blue"
            testId="stat-countries-reached"
          />
          <StatCard
            icon={MapPin}
            label="Ciudades alcanzadas"
            value={analytics?.cities?.length || 0}
            accent="purple"
            testId="stat-cities-reached"
          />
        </div>

        {/* Daily chart */}
        <div
          className="bg-white/5 rounded-xl border border-white/10 p-4 md:p-6"
          data-testid="daily-views-chart"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Vistas diarias</h3>
              <p className="text-white/50 text-xs">Últimos {days} días</p>
            </div>
          </div>
          {analytics?.daily?.length > 0 ? (
            <div className="flex items-end gap-1 h-32">
              {analytics.daily.map((d) => {
                const h = Math.max(4, Math.round((d.count / maxDaily) * 100));
                return (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center justify-end group relative"
                    title={`${d.date}: ${d.count} vistas (${d.unique} únicas)`}
                  >
                    <div
                      className="w-full bg-gradient-to-t from-pink-500 to-orange-400 rounded-t transition-opacity group-hover:opacity-80"
                      style={{ height: `${h}%` }}
                    />
                    <div className="opacity-0 group-hover:opacity-100 absolute -top-9 bg-black/90 border border-white/10 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {d.date}: {d.count} ({d.unique} u.)
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-white/40 text-sm py-8 text-center">
              {isLoadingAnalytics ? "Cargando..." : "Sin datos en este rango"}
            </p>
          )}
        </div>

        {/* Geographic breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TopList
            title="Top países"
            icon={Globe2}
            items={analytics?.countries}
            emptyText="Sin datos geográficos aún"
            testId="top-countries"
            renderLeft={(it) => (
              <>
                <span className="text-lg leading-none">{flagEmoji(it.countryCode)}</span>
                <span className="truncate">{it.value || "Desconocido"}</span>
              </>
            )}
          />
          <TopList
            title="Top regiones"
            icon={MapPin}
            items={analytics?.regions}
            emptyText="Sin datos de regiones"
            testId="top-regions"
            renderLeft={(it) => (
              <>
                <span className="text-lg leading-none">{flagEmoji(it.countryCode)}</span>
                <div className="min-w-0">
                  <p className="truncate">{it.value || "—"}</p>
                  <p className="text-xs text-white/40 truncate">{it.country || ""}</p>
                </div>
              </>
            )}
          />
          <TopList
            title="Top ciudades"
            icon={Building2}
            items={analytics?.cities}
            emptyText="Sin datos de ciudades"
            testId="top-cities"
            renderLeft={(it) => (
              <div className="min-w-0">
                <p className="truncate">{it.value || "—"}</p>
                <p className="text-xs text-white/40 truncate">
                  {it.region ? `${it.region}, ` : ""}
                  {it.country || ""}
                </p>
              </div>
            )}
          />
        </div>

        {/* Devices, browsers, OS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TopList
            title="Dispositivos"
            icon={Smartphone}
            items={analytics?.devices}
            emptyText="Sin datos"
            testId="top-devices"
            renderLeft={(it) => (
              <>
                {deviceIcon(it.value)}
                <span className="capitalize truncate">{it.value || "—"}</span>
              </>
            )}
          />
          <TopList
            title="Navegadores"
            icon={Compass}
            items={analytics?.browsers}
            emptyText="Sin datos"
            testId="top-browsers"
          />
          <TopList
            title="Sistemas operativos"
            icon={Monitor}
            items={analytics?.operatingSystems}
            emptyText="Sin datos"
            testId="top-os"
          />
        </div>

        {/* Referrers + Per creator */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList
            title="Top referrers"
            icon={Link2}
            items={analytics?.referrers}
            emptyText="Sin tráfico de referencia aún"
            testId="top-referrers"
          />
          <TopList
            title={`Top modelos (${days}d)`}
            icon={Crown}
            items={(analytics?.perCreator || []).map((c) => ({
              value: c.username,
              count: c.count,
              unique: c.unique,
              _username: c.username,
            }))}
            emptyText="Sin vistas en este rango"
            testId="top-creators-period"
            renderLeft={(it) => (
              <button
                onClick={() => setCreatorFilter(it._username)}
                className="truncate text-left hover:text-pink-300 hover:underline transition-colors"
                title="Filtrar por esta modelo"
              >
                @{it.value}
              </button>
            )}
          />
        </div>

        {/* All-time rankings */}
        <div
          className="bg-white/5 rounded-xl border border-white/10 p-4 md:p-6"
          data-testid="all-time-rankings"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Ranking histórico por vistas</h2>
              <p className="text-white/50 text-sm">Las modelos con más visualizaciones acumuladas</p>
            </div>
          </div>

          {stats?.creatorRankings?.length > 0 ? (
            <div className="space-y-2">
              {stats.creatorRankings.map((creator, index) => (
                <div
                  key={creator.username}
                  onClick={() => setCreatorFilter(creator.username)}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                  data-testid={`ranking-row-${creator.username}`}
                  title="Ver analítica detallada de esta modelo"
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                      index === 0
                        ? "bg-yellow-500 text-black"
                        : index === 1
                        ? "bg-gray-400 text-black"
                        : index === 2
                        ? "bg-orange-600 text-white"
                        : "bg-white/10 text-white/60"
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                    {creator.avatar ? (
                      <img
                        src={creator.avatar}
                        alt={creator.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">
                        {creator.name?.charAt(0) || "?"}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{creator.name}</p>
                    <p className="text-white/40 text-xs">@{creator.username}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-white font-semibold text-sm">{creator.views || 0}</p>
                    <p className="text-white/40 text-xs">vistas</p>
                  </div>
                  {creator.featured && (
                    <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-white/40">
              <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aún no hay datos de visualización</p>
              <p className="text-sm">Las vistas se registrarán cuando los usuarios visiten los perfiles</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminStats;
